import { useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline, Delete } from '@mui/icons-material'
import {
  CustomTableCell,
  TableHead,
} from '../../components'
import {
  ShiftListProvider,
  useShiftList,
} from '../../contexts/ShiftListContext'
import useViewport from '../../hooks/useViewport'
import { getComparator } from '../../utils/graph'
import { RequestService } from '../../services/RequestService'
import { useSnackbar } from '../../hooks'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const DeleteButton = styled(Delete)`
  color: #888;
  cursor: pointer;
  font-size: 32px;
  padding: 2px;
  &:hover {
    color: red;
  }
`

const rowsPerPage = 10

const ShiftListPage = () => {
  const [order, setOrder] = useState('asc')
  const [page, setPage] = useState(0)
  const { width } = useViewport()
  const { successSnackbar, errorSnackbar } = useSnackbar()
  const { t } = useTranslation('common')
  const headState = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.name'),
      align: 'left',
    },
    {
      id: 'starting_day',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.starting_day'),
      align: 'left',
    },
    {
      id: 'starting_weekday',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.starting_weekday'),
      align: 'left',
    },
    {
      id: 'start_date',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.start_date'),
      align: 'left',
    },
    {
      id: 'duration',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.duration'),
      align: 'left',
    },
    {
      id: 'recurrence',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.recurrence'),
      align: 'left',
    },
    // {
    //   id: 'exceptions',
    //   numeric: false,
    //   disablePadding: false,
    //   label: 'Exceções',
    //   align: 'left',
    // },
    {
      id: 'daily_shift_pattern',
      numeric: false,
      disablePadding: false,
      label: t('list.schedule.header.daily_shift_pattern'),
      align: 'left',
    },
  ]
  const [orderBy, setOrderBy] = useState(headState[0].id)

  const {
        rows,
        loading: loadingData,
      } = useShiftList()

  const weekdayMap = {
    0: t('list.schedule.weekday_map.sunday'),
    1: t('list.schedule.weekday_map.monday'),
    2: t('list.schedule.weekday_map.tuesday'),
    3: t('list.schedule.weekday_map.wednesday'),
    4: t('list.schedule.weekday_map.thursday'),
    5: t('list.schedule.weekday_map.friday'),
    6: t('list.schedule.weekday_map.saturday'),
  }

  const handleDelete = async (id) => {
    if (window.confirm(t('list.schedule.delete_confirm'))) {
      try {
        const response = await RequestService.deleteSchedule(id)

        if (response.status === RequestService.STATUSES.SUCCESS) {
          window.location.reload()
        } else {
          errorSnackbar({ text: response.data?.message || t('list.schedule.associated_error') })
        }

      } catch (e) {
        errorSnackbar({ text: t('list.schedule.associated_error') })
      }
    }
  }

  const isWeekdayRange = (days, recurrence) => {
    days.every(day => day >= 0 && day <= 6) && (recurrence === 'semanal' || recurrence === 'weekly')
  }

  const formatDailyShiftPattern = (patterns, recurrence) => {
    if (!Array.isArray(patterns) || patterns.length === 0) return ''

    return patterns
      .map((pattern) => {
        const days = pattern.days || []
        const timeRanges = pattern.time_ranges || []

        const dayString = isWeekdayRange(days, recurrence)
          ? days.map(day => weekdayMap[day] || `?${day}`).join(', ')
          : `Dias ${days.join(', ')}`

        const timeString = timeRanges
          .map(t => `${t.start_time}–${t.end_time}`)
          .join(', ')

        return `${dayString} ${timeString}`
      })
      .join('; ')
  }

  const formatExceptions = (exceptions) => {
    if (!Array.isArray(exceptions) || exceptions.length === 0) return ''

    return exceptions
      .map((entry) => {
        const date = entry.date || '?'
        const timeRanges = (entry.time_ranges || [])
          .map(tr => `${tr.start_time}–${tr.end_time}`)
          .join(', ')
        return `${date} ${timeRanges}`
      })
      .join('; ')
  }

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  const handleButtonClick = () => {
    openActionsModal()
  }

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {t('list.schedule.title')}
            </span>
            <Tooltip
              title={t('list.schedule.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
        </Header>
        <BodyContainer>
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby='notificationsTable'
            >
              <TableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                headCells={headState}
              />
              <TableBody>
                {!loadingData ? (
                  <>
                    {rows.length > 0 ? (
                      <>
                        {visibleRows.map(row => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={row.name}
                            >
                              <CustomTableCell
                                data={row.name}
                                type='text'
                                sx={{ cursor: 'pointer' }}
                              />
                              <CustomTableCell
                                data={row.starting_day}
                                type='text'
                                sx={{ cursor: 'pointer' }}
                                 />
                              <CustomTableCell data={row.starting_weekday} />
                              <CustomTableCell data={row.start_date} />
                              <CustomTableCell data={row.duration} />
                              <CustomTableCell data={row.recurrence} />
                              <CustomTableCell
                                data={formatDailyShiftPattern(row.daily_shift_pattern, row.recurrence)}
                              />
                              <TableCell align="center">
                                <DeleteButton
                                  onClick={() => handleDelete(row?.id)}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                        {emptyRows() > 0 && (
                          <TableRow
                            style={{
                              height: 53 * emptyRows(),
                            }}
                          >
                            <TableCell colSpan={5} />
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <TableRow style={{ height: 53 * 10 }}>
                        <TableCell
                          colSpan={5}
                          align='center'
                        >
                          {t('no_data')}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <TableRow style={{ height: 53 * 10 }}>
                    <TableCell
                      colSpan={5}
                      align='center'
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={rows.length}
            showFirstButton
            showLastButton
            labelRowsPerPage={''}
            rowsPerPageOptions={[]}
            rowsPerPage={10}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ to, count }) =>
              {t('list.footer', {to: to, count: count})}
            }
          />
        </BodyContainer>
      </ContentContainer>
    </Container>
  )
}

const ShiftList = () => {
  return (
      <ShiftListProvider>
        <ShiftListPage />
      </ShiftListProvider>
  )
}

export default ShiftList
